// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Pokémon Universe - Star Systems Configuration
 * 
 * A universe where Pokémon have evolved across different planets.
 * Each region from the games is now a star system with unique Pokémon ecosystems.
 */

export const starSystems = [
  // CENTER - Kanto Prime at visual center (45, 50)
  {
    id: 600,
    name: 'Kanto Prime',
    type: 'planet',
    description: 'The original 151. Where it all began. A balanced ecosystem of classic Pokémon.',
    distance: 0,
    coordinates: { x: 45, y: 50, z: 0 },
    threat: 'LOW',
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'weapons', 'upgrades', 'modules'],
    tradeType: 'agricultural',
    primaryColor: '#FF6B6B',
    secondaryColor: '#FFE66D',
    hasRings: false,
    aliens: ['pikachu', 'eevee', 'charizard', 'mewtwo', 'team-rocket']
  },
  
  // INNER RING - 9-12 LY from center (spiral pattern starting north, going clockwise)
  {
    id: 610,
    name: 'Pokémon League Station',
    type: 'station',
    description: 'Massive space station. The ultimate challenge for trainers across all regions.',
    distance: 9.4,
    coordinates: { x: 46, y: 59, z: 3 },  // North (9.2 LY from center)
    threat: 'LOW',
    resources: 'FUEL DEPOT',
    services: ['refuel', 'repairs', 'upgrades', 'weapons', 'trade', 'modules'],
    tradeType: 'station_advanced',
    primaryColor: '#FFD700',
    secondaryColor: '#C0C0C0',
    hasRings: false,
    aliens: ['cynthia', 'leon'] // Elite trainers at the League
  },
  {
    id: 601,
    name: 'Johto System',
    type: 'planet',
    description: 'Ancient traditions meet new discoveries. Home to legendary beasts and time-traveling Pokémon.',
    distance: 10.8,
    coordinates: { x: 52, y: 43, z: 2 },  // Southeast (10.6 LY from center)
    threat: 'LOW',
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade'],
    tradeType: 'agricultural',
    primaryColor: '#C9A0DC',
    secondaryColor: '#FFD700',
    hasRings: false,
    aliens: ['pikachu', 'eevee', 'team-rocket']
  },
  
  // MIDDLE RING - 16-20 LY from center (continuing spiral)
  {
    id: 602,
    name: 'Hoenn Archipelago',
    type: 'planet',
    description: 'Water world with volcanic islands. Primal energies surge beneath the waves.',
    distance: 17.7,
    coordinates: { x: 60, y: 58, z: -4 },  // Northeast (17.5 LY from center)
    threat: 'MODERATE',
    resources: 'VERY HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'weapons'],
    tradeType: 'mining',
    primaryColor: '#4A90E2',
    secondaryColor: '#E74C3C',
    hasRings: false,
    aliens: ['rayquaza']
  },
  {
    id: 604,
    name: 'Unova Metropolis',
    type: 'station',
    description: 'Advanced urban planet. Technology and Pokémon coexist in gleaming cities.',
    distance: 18.4,
    coordinates: { x: 55, y: 35, z: 3 },  // South-southeast (18.3 LY from center)
    threat: 'LOW',
    resources: 'MEDIUM',
    services: ['refuel', 'repairs', 'trade', 'weapons', 'upgrades', 'modules'],
    tradeType: 'industrial',
    primaryColor: '#34495E',
    secondaryColor: '#3498DB',
    hasRings: false,
    aliens: [] // Urban station - no wild Pokémon
  },
  {
    id: 608,
    name: 'Paldea Frontier',
    type: 'planet',
    description: 'Open-world planet with diverse biomes. Terastal phenomena crystallize Pokémon.',
    distance: 16.2,
    coordinates: { x: 33, y: 60, z: 5 },  // Northwest (16.1 LY from center)
    threat: 'MODERATE',
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'weapons'],
    tradeType: 'mining',
    primaryColor: '#FFEB3B',
    secondaryColor: '#00E676',
    hasRings: false,
    aliens: [] // Frontier world - sparse population
  },
  
  // OUTER RING - 23-28 LY from center (continuing spiral)
  {
    id: 603,
    name: 'Sinnoh Highlands',
    type: 'planet',
    description: 'Mountainous world where space and time converge. Ancient ruins hold cosmic secrets.',
    distance: 24.4,
    coordinates: { x: 65, y: 38, z: 8 },  // East-southeast (24.3 LY from center)
    threat: 'HIGH',
    resources: 'VERY HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'weapons'],
    tradeType: 'mining',
    primaryColor: '#7F8C8D',
    secondaryColor: '#9B59B6',
    hasRings: true,
    aliens: ['lucario', 'dialga', 'palkia', 'giratina', 'cynthia']
  },
  {
    id: 605,
    name: 'Kalos Nebula',
    type: 'planet',
    description: 'Elegant world of beauty and power. Mega Evolution energy radiates from the core.',
    distance: 22.8,
    coordinates: { x: 40, y: 28, z: -3 },  // Southwest (22.8 LY from center)
    threat: 'MODERATE',
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'weapons', 'upgrades'],
    tradeType: 'luxury',
    primaryColor: '#E91E63',
    secondaryColor: '#00BCD4',
    hasRings: true,
    aliens: ['greninja']
  },
  {
    id: 607,
    name: 'Galar Expanse',
    type: 'planet',
    description: 'Industrial powerhouse with wild areas. Dynamax energy storms rage across the landscape.',
    distance: 26.9,
    coordinates: { x: 23, y: 62, z: -6 },  // Northwest (26.9 LY from center)
    threat: 'MODERATE',
    resources: 'VERY HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'weapons', 'upgrades'],
    tradeType: 'industrial',
    primaryColor: '#673AB7',
    secondaryColor: '#FF5722',
    hasRings: false,
    aliens: ['charizard', 'leon']
  },
  {
    id: 606,
    name: 'Alola Paradise',
    type: 'planet',
    description: 'Tropical island world. Ultra Wormholes occasionally tear reality here. Return gate to Milky Way.',
    distance: 25.5,
    coordinates: { x: 48, y: 74, z: 2 },  // North (25.5 LY from center, 8 LY from gate)
    threat: 'HIGH',
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'weapons'],
    tradeType: 'agricultural',
    primaryColor: '#FF9800',
    secondaryColor: '#4CAF50',
    hasRings: false,
    aliens: ['ultra-beast']
  },
  
  // FAR OUTER - 32 LY (dangerous frontier)
  {
    id: 609,
    name: 'Orre Wasteland',
    type: 'planet',
    description: 'Desert world scarred by ancient wars. Shadow Pokémon experiments left dark marks.',
    distance: 32.2,
    coordinates: { x: 27, y: 24, z: -8 },  // Southwest (32.2 LY from center)
    threat: 'VERY HIGH',
    resources: 'LOW',
    services: ['refuel', 'repairs', 'trade', 'weapons'],
    tradeType: 'remote',
    primaryColor: '#D4A574',
    secondaryColor: '#8B4513',
    hasRings: false,
    aliens: [] // Barren wasteland - no life
  },

  // GRAVITATIONAL ANOMALY - Narrative explanation for distance compression
  {
    id: 699,
    name: 'Ultra Space Singularity',
    type: 'blackhole',
    description: 'Mysterious gravitational anomaly at the heart of the Pokémon universe. Ultra Wormholes emanate from this point, warping space and making distant worlds accessible.',
    distance: 50000, // Theoretical distance - unreachable
    coordinates: { x: 42, y: 52, z: -12 },  // Within planetary cluster, deeper in space
    threat: 'EXTREME',
    resources: 'ULTRA ENERGY',
    services: [],
    tradeType: 'none',
    primaryColor: '#000000',
    secondaryColor: '#FF00FF',
    hasRings: true
  }
];

// Star positions for map visualization
export const starPositions = starSystems.reduce((acc, system) => {
  acc[system.id] = system.coordinates;
  return acc;
}, {});
